<script setup>
import {reactive,onMounted,ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const compName = 'PayPeriodPersonReleasedComponent';

const props = defineProps({
  contextIdA: String,
  contextIdB: String
});

const form = ref(null);

const data = reactive({
  defaultItem: {
    amount: null,
    description: null,
  },
  headers: [
    { title: 'Date', key: 'date' },
    { title: 'Amount', key: 'amount' },
    { title: 'Description', key: 'description' },
    { title: 'Actions', key: 'actions', sortable: false }
  ],
  isDisabled: false,
  isEditing: false,
  items: [],
  loading: false,
  newItem: {
    amount: null,
    description: null,
  },
  openAdjustmentForm: false,
  search: null,
  valid: true,
});

const deleteItem = (item) => {
  let deleteMessage = confirm(
      'Are you sure you want to delete this item?'
  )
  if (deleteMessage === true) {
    data.loading = true
    data.openAdjustmentForm = false
    ApiWrapper.post('adjustments', 'delete-adjustment', {
          id: item.id
        })
        .then((response) => {
          getData();

          ToastUtil.error('Deleted adjustment');
        })
        .catch((e)=>{
          ToastUtil.error(e);
          data.loading = false;
        })
  }
}

const discardItem = () => {
  if (data.isEditing) {
    getData()
  }
  data.openAdjustmentForm = false
  data.isEditing = false
  data.newItem = Object.assign({}, data.defaultItem)
}

const editItem = (item) => {
  data.isEditing = true
  data.openAdjustmentForm = true
  data.newItem = JSON.parse(JSON.stringify(item))
}

const formatISODateToAmerican = (date) =>{
  if (!date) return null
  const [year, month, day] = date.split('-')
  return `${month}/${day}/${year}`
}

const getData = () => {
  data.isDisabled = false;
  let contextIdA = props.contextIdA;
  let contextIdB = props.contextIdB;

  if (!contextIdB) {
    ApiWrapper.post('adjustments', 'my-pay-period', {payPeriodId: contextIdA})
    .then((response) => {
      if(response){
        if(response.adjustments){
          data.items = response.adjustments
        }
      }
    })
    .catch((e)=>{
      ToastUtil.error(e);
    })
    .finally(() => {
      data.loading = false;
    });
  } else {
    ApiWrapper.get(
            'adjustments',
            'pay-period-person','payPeriodId=' +
            contextIdA +
            '&personId=' +
            contextIdB
        )
        .then((response) => {
          data.items = response.adjustments
        })
        .catch((e)=>{
          ToastUtil.error(e);
        })
        .finally(() => {
          data.loading = false
        });
  }
}

const saveItem = () => {
  if (form.value.validate()) {
    data.openAdjustmentForm = false;
    data.isDisabled = true;
    data.loading = true;

    let contextIdA = props.contextIdA;
    let contextIdB = props.contextIdB;

    if (data.isEditing) {
      ApiWrapper.post('adjustments', 'edit-adjustment', {
            editedItem: data.newItem
          })
          .then((response) => {
            getData();
            discardItem();

            ToastUtil.successMsg('Edited adjustment');
          })
          .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
            data.isDisabled = false;
          });
    } else {
      ApiWrapper.post('adjustments', 'create', {
            newAdjustment: data.newItem,
            payPeriodId: contextIdA,
            personId: contextIdB
          })
          .then((response) => {
            getData();
            discardItem();

            ToastUtil.successMsg('Created adjustment');
          })
          .catch((err) => {
            ToastUtil.error(err);

            data.loading = false;
            data.isDisabled = false;
          });
    }
  }
}

onMounted(()=>{
  getData()
})
</script>

<template>
  <v-container fluid>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Adjustments</v-toolbar-title>
        <v-progress-linear
            :active="data.loading"
            :indeterminate="data.loading"
            absolute
            bottom
            color="primary"
        ></v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-btn
            size="small"
            variant="elevated"
            color="primary"
            @click="data.openAdjustmentForm = true"
            v-if="Permissions.checkPermissions(['adjustments.create'])"
        >
          New Adjustment
        </v-btn>
      </v-toolbar>

      <v-form
          ref="form"
          v-model="data.valid"
          lazy-validation
          v-if="data.openAdjustmentForm"
      >
        <v-card-title v-if="!data.isEditing" class="primary--text">New Adjustment</v-card-title>
        <v-card-title v-if="data.isEditing" class="primary--text">Edit Adjustment</v-card-title>
        <v-row class="px-5">
          <v-col cols="12" sm="3">
            <v-text-field
                type="number"
                v-model="data.newItem.amount"
                label="Amount"
                :rules="[v => !!v || 'Amount is required']"
                prepend-inner-icon="mdi-currency-usd"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field
                v-model="data.newItem.description"
                label="Description"
                :rules="[v => !!v || 'Description is required']"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn size="small" variant="elevated" class="px-2" @click="saveItem" color="primary" :disabled="!data.valid || data.isDisabled === true">
              <v-icon color="white">mdi-plus</v-icon>
            </v-btn>
            <v-btn size="small" variant="elevated" class="px-2" @click="discardItem" color="error" :disabled="data.isDisabled === true">
              <v-icon color="white">mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
      <v-row>
        <v-col cols="6">
          <v-text-field
              v-model="data.search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              class="ml-4"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-data-table
          :items="data.items"
          :headers="data.headers"
          :search="data.search"
          loading-text="Loading... Please wait"
      >
        <template v-slot:[`item.date`]="{ item }">
          {{ formatISODateToAmerican(item.date) }}
        </template>
        <template v-slot:[`item.amount`]="{ item }">
          {{ '$' + item.amount }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon v-if="Permissions.checkPermissions(['adjustments.update'])" class="mr-2" @click="editItem(item)">mdi-pencil </v-icon>
          <v-icon v-if="Permissions.checkPermissions(['adjustments.delete'])" @click="deleteItem(item)">mdi-delete </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}
[v-cloak] {
  display: none;
}
</style>